import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../seo';
import { seoConfigs } from '../../utils/seo-configs';

function HowItWorksPage() {
  const steps = [
    {
      id: 1,
      title: "Search & Filter",
      description: "Search for therapists by location, specialty, insurance, or specific needs. Use our advanced filters to find the perfect match.",
      icon: "🔍",
      details: [
        "Search by location, specialty, or insurance",
        "Filter by session type (online/in-person)",
        "Sort by experience, ratings, or availability",
        "View detailed therapist profiles"
      ]
    },
    {
      id: 2,
      title: "Connect & Contact",
      description: "Review therapist profiles, read about their approach, and reach out directly through our secure contact system.",
      icon: "💬",
      details: [
        "Read detailed therapist profiles and specialties",
        "View session rates and insurance information",
        "Send secure messages through our platform",
        "Schedule initial consultations"
      ]
    },
    {
      id: 3,
      title: "Start Your Journey",
      description: "Begin therapy sessions with your chosen therapist and take the first step toward better mental health.",
      icon: "🌱",
      details: [
        "Attend your first session (online or in-person)",
        "Work with your therapist on your goals",
        "Track your progress over time",
        "Access ongoing support and resources"
      ]
    }
  ];

  const benefits = [
    {
      title: "Licensed Professionals Only",
      description: "All therapists on our platform are licensed mental health professionals.",
      icon: "✅"
    },
    {
      title: "Insurance Friendly",
      description: "Find therapists who accept your insurance or offer affordable out-of-network options.",
      icon: "🏥"
    },
    {
      title: "Secure & Private",
      description: "Your personal information and communications are protected with industry-standard security.",
      icon: "🔒"
    },
    {
      title: "Multiple Specialties",
      description: "Find therapists specialized in anxiety, depression, trauma, couples therapy, and more.",
      icon: "🎯"
    }
  ];

  return (
    <>
      <SEOHead {...seoConfigs.howItWorks} />
      
      <div className="min-h-screen bg-[#e4fde1]" style={{ marginTop: '-200px', paddingTop: '200px' }}>
        {/* Hero Section */}
        <div className="bg-[#e4fde1] py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-[#254e70]" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
              How TherapyList Works
            </h1>
            <p className="text-xl text-[#254e70]/80 mb-8 max-w-3xl mx-auto">
              Finding the right therapist shouldn't be complicated. Our simple 3-step process connects you with licensed mental health professionals who match your specific needs.
            </p>
            <Link 
              to="/find-therapists"
              className="inline-flex items-center gap-2 bg-[#254e70] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#9dacff] hover:text-white transition-all duration-200"
            >
              Start Your Search
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Steps Section */}
        <div className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                3 Simple Steps to Better Mental Health
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Our streamlined process makes it easy to find and connect with the right therapist for your needs.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {steps.map((step, index) => (
                <div key={step.id} className="relative">
                  {/* Connection line (hidden on mobile) */}
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-16 -right-4 w-8 h-0.5 bg-[#9dacff] z-10"></div>
                  )}
                  
                  <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 h-full">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-[#9dacff] rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
                        {step.icon}
                      </div>
                      <div className="w-8 h-8 bg-[#254e70] text-white rounded-full flex items-center justify-center text-sm font-bold mx-auto mb-4">
                        {step.id}
                      </div>
                      <h3 className="text-xl font-bold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                        {step.title}
                      </h3>
                    </div>
                    
                    <p className="text-gray-600 mb-6 text-center">
                      {step.description}
                    </p>
                    
                    <ul className="space-y-2">
                      {step.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-start gap-2 text-sm text-gray-600">
                          <svg className="w-4 h-4 text-[#9dacff] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                Why Choose TherapyList?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We're committed to making mental health care accessible, safe, and effective for everyone.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center p-6 rounded-lg hover:bg-[#e4fde1] transition-colors duration-200">
                  <div className="text-3xl mb-4">{benefit.icon}</div>
                  <h3 className="text-lg font-semibold text-[#254e70] mb-2" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-lg font-semibold text-[#254e70] mb-2">
                  How do I know if a therapist is right for me?
                </h3>
                <p className="text-gray-600">
                  Each therapist profile includes their specialties, approach, experience, and personal philosophy. Many offer brief consultations to help you determine if they're a good fit for your needs.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-lg font-semibold text-[#254e70] mb-2">
                  Does my insurance cover therapy sessions?
                </h3>
                <p className="text-gray-600">
                  Many insurance plans cover mental health services. You can filter therapists by those who accept your insurance, and our platform shows which providers are in-network for your plan.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-lg font-semibold text-[#254e70] mb-2">
                  Can I have sessions online or do I need to meet in person?
                </h3>
                <p className="text-gray-600">
                  Many of our therapists offer both online and in-person sessions. You can filter by session type and choose what works best for your schedule and comfort level.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-lg font-semibold text-[#254e70] mb-2">
                  How long does it take to find a therapist?
                </h3>
                <p className="text-gray-600">
                  You can search and contact therapists immediately. Response times vary, but most therapists respond within 24-48 hours. Some offer same-week or next-week appointments.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-16 bg-[#9dacff] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
              Ready to Start Your Mental Health Journey?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Take the first step toward better mental health. Find licensed therapists in your area today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/find-therapists"
                className="inline-flex items-center justify-center gap-2 bg-[#254e70] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-[#254e70] transition-all duration-200"
              >
                Find Therapists Near You
              </Link>
              <Link 
                to="/specialties"
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-[#9dacff] transition-all duration-200"
              >
                Browse Specialties
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default HowItWorksPage; 